import { useEffect, useRef, useState } from 'react'
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material'
import { BUTTON_RADIUS } from '../../../theme/tokens'

/**
 * PromptDialog — Eingabe-Dialog fuer Freitext, der SPAETER gelesen wird.
 *
 * @description
 *   `window.prompt` war die letzte Browser-Flaeche mit einem Eingabefeld. Es
 *   sieht aus wie der Browser, nicht wie POG, kennt keine Design-Tokens, hat
 *   keine Ueberschrift — und es liefert bei Abbruch `null` und bei einer
 *   LEEREN Zeile eine leere Zeichenkette, ohne die beiden zu unterscheiden.
 *
 *   `ConfirmDialog` kann das nicht uebernehmen: er hat kein Eingabefeld und
 *   nennt genau das als seinen Randfall (`ConfirmDialog.jsx`, `@dontUseFor`).
 *   Ein Toast kann es ebenfalls nicht — er nimmt keine Antwort entgegen.
 *
 *   Dieser Baustein ist deshalb KEIN fuenfter Bestaetigungsbaustein: er
 *   bestaetigt nichts, er sammelt Text. Die zwei Knoepfe sind die Form, die
 *   `window.prompt` hatte (OK/Abbrechen), nur in der Sprache des Hauses.
 *
 *   Anlass: UI-Durchgang 2, Teil 6, K-D (M-13) — 19 Browser-Dialoge in 9
 *   Produktionsdateien, davon zwei `prompt`.
 *
 * @useWhen
 *   - Freitext, den ein Mensch spaeter liest (Grund, Notiz, Begruendung)
 *   - Eine Zeile oder ein kurzer Absatz, keine Auswahl und kein Formular
 *   - Ein Abbruch muss moeglich sein, ohne etwas zu schreiben
 *
 * @dontUseFor
 *   - Eine Rueckfrage ohne Eingabe → `ConfirmDialog`
 *   - Mehrere Felder oder eine Auswahl → eigener Dialog
 *   - Eine Meldung, die keine Antwort braucht → `toast` aus `sonner`
 *
 * @related
 *   - ConfirmDialog — dieselbe Huelle, ohne Feld; die Abgrenzung steht dort
 *   - InlineConfirmButton — Rueckfrage am Knopf, ohne Eingabe
 *
 * @props
 *   open         boolean            Steuert die Sichtbarkeit.
 *   title        string             Ueberschrift; nennt den GEGENSTAND.
 *   label        string             Feldbeschriftung (Vorgabe "Value").
 *   placeholder  string             Beispieltext im leeren Feld.
 *   initialValue string             Vorbelegung; wird bei jedem Oeffnen gesetzt.
 *   required     boolean            Leeres Feld sperrt das Absenden (Vorgabe true).
 *   multiline    boolean            Mehrzeiliges Feld (Vorgabe false).
 *   confirmLabel string             Beschriftung des Absendens (Vorgabe "Save").
 *   cancelLabel  string             Beschriftung des Abbruchs (Vorgabe "Cancel").
 *   busy         boolean            Sperrt beide Knoepfe und das Feld.
 *   onSubmit     (text) => void     Wird mit dem GETRIMMTEN Text gerufen.
 *   onCancel     () => void         Wird bei Abbruch, Esc und Klick daneben gerufen.
 */
export default function PromptDialog({
  open,
  title,
  label = 'Value',
  placeholder,
  initialValue = '',
  required = true,
  multiline = false,
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  busy = false,
  onSubmit,
  onCancel,
}) {
  const [value, setValue] = useState(initialValue)

  // 🔴 R15-W7: zurueckgesetzt wird beim OEFFNEN, nicht bei jeder Aenderung von
  //    `initialValue`. Stand der Wert in den Abhaengigkeiten (wie in der ersten
  //    Fassung), verlor ein Aufrufer mit einem nicht-konstanten `initialValue`
  //    die Eingabe MITTEN IM TIPPEN. Heute uebergibt kein Aufrufer
  //    `initialValue` — die Falle war scharf, aber unbetreten.
  //
  //    `warOffen` statt einer reinen `[open]`-Liste: die braeuchte eine
  //    eslint-Unterdrueckung fuer `initialValue`, und die waere hier unehrlich —
  //    der Wert wird gelesen, nur nicht bei jeder Aenderung.
  //
  // Ohne das Zuruecksetzen beim Oeffnen traegt der Dialog den Text des vorigen
  // Aufrufs weiter — er bleibt zwischen zwei Aufrufen montiert, und
  // `window.prompt` hat ihn nie behalten.
  const warOffen = useRef(false)
  useEffect(() => {
    if (open && !warOffen.current) setValue(initialValue)
    warOffen.current = open
  }, [open, initialValue])

  // 🔴 Der Unterschied zu `window.prompt`, und er ist Absicht: eine leere
  //    Zeile war dort eine gueltige Antwort. Hier sperrt sie das Absenden,
  //    solange `required` gilt — sonst ist der gesammelte Text wertlos.
  const absendenGesperrt = busy || (required && value.trim() === '')

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          margin="dense"
          multiline={multiline}
          minRows={multiline ? 3 : undefined}
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy} sx={{ borderRadius: `${BUTTON_RADIUS}px` }}>
          {cancelLabel}
        </Button>
        <Button
          onClick={() => onSubmit(value.trim())}
          disabled={absendenGesperrt}
          variant="contained"
          sx={{ borderRadius: `${BUTTON_RADIUS}px` }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
